import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { formatNum, apiErr } from "../lib/utils";
import { Plus, Users, Shield, CalendarRange, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { KpiDrilldownDialog } from "../components/KpiDrilldownDialog";
import { BackfillKpiDialog } from "../components/BackfillKpiDialog";

export default function SocialGov() {
  const [summary, setSummary] = useState({ social: [], governance: [] });
  const [catalogue, setCatalogue] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", period: "2024", value: "" });
  const [active, setActive] = useState(null);
  const [backfillOpen, setBackfillOpen] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/esg-kpis/summary"),
      api.get("/esg-kpis/catalogue"),
    ]);
    setSummary(s.data);
    setCatalogue(c.data.kpis);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/esg-kpis/values", { ...form, value: parseFloat(form.value) });
      toast.success("KPI value recorded");
      setOpen(false);
      setForm({ code: "", period: "2024", value: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const resolve = async (k) => {
    if (!window.confirm(`Archive the superseded value(s) for '${k.name}'? The ${k.period} figure (${k.value}) is kept. Archived records are excluded from cards, intensity and exports, and can be restored from Data Management → Archived.`)) return;
    try {
      const r = await api.post(`/esg-kpis/${k.code}/resolve-conflict`);
      toast.success(`Archived ${r.data.archived} superseded record(s) — ${r.data.kept_period} value kept`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };


  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module · ESRS S1 / G1 · SGX Core</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Non-Emission ESG</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Social and Governance KPIs aligned with ESRS S1/G1 and SGX Core Metrics. Click any KPI to drill into its trend, source data and audit trail.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setBackfillOpen(true)} data-testid="backfill-open-btn"><CalendarRange className="h-4 w-4" /> Backfill history</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="primary" data-testid="add-kpi-value-btn"><Plus className="h-4 w-4" /> Record value</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record KPI value</DialogTitle></DialogHeader>
            <div>
              <Label>KPI</Label>
              <Select value={form.code} onValueChange={(v) => setForm({...form, code: v})}>
                <SelectTrigger data-testid="kpi-code-select"><SelectValue placeholder="Choose KPI" /></SelectTrigger>
                <SelectContent>
                  {catalogue.map((k) => (
                    <SelectItem key={k.code} value={k.code}>{k.dimension} · {k.name} ({k.unit})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Period</Label><Input value={form.period} onChange={(e) => setForm({...form, period: e.target.value})} placeholder="2024 or 2024-Q4" data-testid="kpi-period-input" /></div>
            <div><Label>Value</Label><Input type="number" step="any" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} data-testid="kpi-value-input" />
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="kpi-value-help">Enter the TOTAL value for the period — this replaces the period's current figure; it does not add to it. The dashboard card shows the most recent period on record.</p>
            </div>
            <Button variant="primary" onClick={submit} data-testid="kpi-submit-btn">Save</Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Tabs defaultValue="social">
        <TabsList>
          <TabsTrigger value="social" data-testid="tab-social"><Users className="h-3.5 w-3.5 mr-1.5" /> Social ({summary.social.length})</TabsTrigger>
          <TabsTrigger value="governance" data-testid="tab-governance"><Shield className="h-3.5 w-3.5 mr-1.5" /> Governance ({summary.governance.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="social">
          <KPIGrid items={summary.social} onOpen={setActive} onResolve={resolve} />
        </TabsContent>
        <TabsContent value="governance">
          <KPIGrid items={summary.governance} onOpen={setActive} onResolve={resolve} />
        </TabsContent>
      </Tabs>

      <KpiDrilldownDialog
        kpi={active}
        year={2024}
        open={!!active}
        onClose={() => setActive(null)}
        detailEndpoint={(id, y) => `/esg-kpis/${id}/drilldown?year=${y}`}
        commentaryEndpoint={(id, y) => `/esg-kpis/${id}/commentary?year=${y}`}
        accent="#60A5FA"
      />

      <BackfillKpiDialog open={backfillOpen} onOpenChange={setBackfillOpen} onSaved={load} />
    </div>
  );
}

function KpiButton({ k, onOpen, onResolve }) {
  return (
        <button
          key={k.code}
          type="button"
          onClick={() => onOpen({ id: k.code, name: k.name, group: k.dimension })}
          className={`card-flat p-5 text-left hover:border-accent/60 hover:bg-surface-hover transition-colors ${k.value === null ? "opacity-60" : ""}`}
          data-testid={`kpi-${k.code}`}
        >
          <div className="flex items-center justify-between mb-3">
            <Badge variant="outline" className="text-[10px]">{k.code}</Badge>
            <span className="font-mono text-[10px] text-muted-foreground" data-testid={`kpi-asof-${k.code}`}>{k.period ? `as of ${k.period}` : "—"}</span>
          </div>
          <div className="text-sm font-medium leading-tight mb-3">{k.name}</div>
          <div className="font-display font-bold text-3xl">
            {k.value !== null ? formatNum(k.value, {decimals: 1}) : "—"}
            <span className="text-sm text-muted-foreground font-sans font-normal ml-1">{k.unit}</span>
          </div>
          {k.granularity_conflict && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge variant="warning" className="text-[8px] uppercase" data-testid={`kpi-conflict-${k.code}`}
                role="status" aria-label="Granularity conflict"
                title={`${k.precedence_note} Values on record: ${(k.alternatives || []).map((a) => `${a.period}: ${a.value} (${a.source})`).join(" · ")}`}>
                granularity conflict
              </Badge>
              <span role="button" tabIndex={0} data-testid={`kpi-resolve-${k.code}`}
                onClick={(e) => { e.stopPropagation(); onResolve?.(k); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onResolve?.(k); } }}
                className="text-[10px] underline text-muted-foreground hover:text-foreground cursor-pointer"
                title="One click: soft-archive the superseded value(s); the precedence winner is kept. Reversible from Data Management → Archived.">
                archive superseded
              </span>
            </div>
          )}
        </button>
  );
}

function KPIGrid({ items, onOpen, onResolve }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const populated = items.filter((k) => k.value !== null && k.value !== undefined);
  const empty = items.filter((k) => k.value === null || k.value === undefined);
  return (
    <div className="space-y-4">
      {populated.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {populated.map((k) => <KpiButton key={k.code} k={k} onOpen={onOpen} onResolve={onResolve} />)}
        </div>
      ) : (
        <div className="card-flat p-8 text-center text-sm text-muted-foreground" data-testid="socgov-no-populated">
          No values recorded yet in this category. Record a value to populate its card.
        </div>
      )}

      {empty.length > 0 && (
        <div data-testid="socgov-empty-section">
          <button
            type="button"
            onClick={() => setShowEmpty((v) => !v)}
            data-testid="socgov-toggle-empty"
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showEmpty ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Not yet tracked ({empty.length})
          </button>
          {showEmpty && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
              {empty.map((k) => <KpiButton key={k.code} k={k} onOpen={onOpen} onResolve={onResolve} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
